<script setup lang="ts">
import type { CorporatePageApiModel, CorporateStickyBarModel } from '@gro-up/api/corporate'
import type { NewsItemHeaderApiModel } from '@gro-up/api/corporate'
import { useRichTextToHtml } from '@gro-up/ui/composables'
import { computed } from 'vue'

import { useDomain } from '../../composables/use-domain'
import type { HeroComponentAlias } from '../../composables/useComponentMappers'
import NewsItemHeaderBlock from '../blocks/NewsItemHeader/NewsItemHeaderBlock.vue'
import StickyBarBlock from '../blocks/StickyBar/StickyBarBlock.vue'
import StickyBarFlupje from '../blocks/StickyBar/StickyBarFlupje.vue'

import PageBlock from './PageBlock.vue'

import { useRuntimeConfig } from '#app'
import { useSeoMeta, useHead } from '#imports'

export type PageProps = {
  page: CorporatePageApiModel
}

const props = defineProps<PageProps>()

const domain = useDomain()
const config = useRuntimeConfig()

useHead({
  htmlAttrs: {
    lang: 'nl',
  },
  link: [
    {
      rel: 'canonical',
      href: props.page.canonicalUrl,
    },
  ],
  meta: [
    {
      name: 'robots',
      content:
        config.public.indexable === false
          ? 'noindex, nofollow'
          : `${props.page.noIndex ? 'no' : ''}index, ${props.page.noFollow ? 'no' : ''}follow`,
    },
  ],
  script: props.page.structuredData?.map(data => ({
    type: 'application/ld+json',
    textContent: data,
  })),
})

useSeoMeta({
  title: `${props.page.meta?.title ?? 'gro-up'}`,
  description: props.page.meta?.description,
  ogUrl: props.page.canonicalUrl,
  ogTitle: `${props.page.name ?? 'gro-up'}`,
  ogDescription: props.page.meta?.description,
  ogType: 'website',
  ogImage: props.page.og?.image?.src,
  ogImageAlt: props.page.og?.image?.altText,
  ogImageWidth: props.page.og?.image?.width,
  ogImageHeight: props.page.og?.image?.height,
  ogLocale: 'nl_NL',
  ogSiteName: props.page.siteName ?? 'gro-up',
  twitterCard: 'summary_large_image',
  twitterTitle: `${props.page.twitter?.title ?? 'gro-up'}`,
  twitterDescription: props.page.twitter?.description,
  twitterImage: props.page.twitter?.image?.src,
  twitterImageAlt: props.page.twitter?.image?.altText,
  themeColor: domain.value
    ? {
        'child-care': '#FBF5EA',
        'head-office': '#FAF8F5',
        'community-work': '#FFE5E5',
        'youth-support': '#E9F3F4',
        'maternity-care': '#F8EBD7',
      }[domain.value]
    : '#1F122E',
})

const HERO_ALIASES: HeroComponentAlias[] = ['subHomeHeader', 'locationHero', 'locationHeader', 'neighbourhoodHeader']
const blocks = computed(() =>
  props.page.blocks?.filter(
    block => !HERO_ALIASES.includes(block.type as HeroComponentAlias) && block.type !== 'stickyBar' && block.type !== 'newsItemHeader',
  ),
)
const hero = computed<NewsItemHeaderApiModel | undefined>(
  () => ((props.page.blocks?.find(block => block.type === 'newsItemHeader')?.value) as NewsItemHeaderApiModel) ,
)

const stickyBarData = computed<CorporateStickyBarModel | undefined>(() => {
  const block = props.page.blocks?.find(block => block.type === 'stickyBar')
  if (!block) {
    return
  }
  return block.value as CorporateStickyBarModel
})

const { richTextToHtml } = useRichTextToHtml()

const hasStickyBar = computed<boolean>(() => !!stickyBarData.value)

const flupjeTitle = computed<string | undefined>(() => {
  const item = stickyBarData.value?.item
  if (!item) {
    return undefined
  }
  if ('title' in item) {
    return item.title
  }
  if ('header' in item) {
    return richTextToHtml(item.header)
  }
  return undefined
})
</script>

<template>
  <div class="flex grow flex-col">
    <div class="components relative z-10">
      <div class="relative z-10 flex grow flex-col gap-16 lg:gap-32">
        <NewsItemHeaderBlock v-if="hero" v-bind="hero" />

        <StickyBarBlock v-if="hasStickyBar && stickyBarData" v-bind="stickyBarData" variant="mobile" />
        <PageBlock v-for="(block, index) in blocks" :key="`page-block-${index}`" :block="block" />
      </div>
    </div>

    <ClientOnly>
      <StickyBarBlock v-if="hasStickyBar && stickyBarData" v-bind="stickyBarData" variant="desktop" />
      <StickyBarFlupje v-if="hasStickyBar" :title="flupjeTitle" />
    </ClientOnly>
  </div>
</template>
